import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getPublicSurveyInstance,
  createAnonymousSession,
  submitAnswer,
  completeSurveyResponse,
  getSurveyResponse,
} from '../api/surveyResponseApi';
import { CreateSurveyResponseDTO, SubmitAnswerDTO } from '../dtos';
import { surveyResponseQueryKeys } from './queryKeys';

/**
 * Hook to get public survey instance data (no auth required)
 */
export const usePublicSurveyInstance = (instanceId: string, surveyId: string, enabled = true) => {
  return useQuery({
    queryKey: [...surveyResponseQueryKeys.publicInstance(instanceId), surveyId],
    queryFn: () => getPublicSurveyInstance(instanceId, surveyId),
    enabled: !!instanceId && !!surveyId && enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

/**
 * Hook to create anonymous session
 */
export const useCreateAnonymousSession = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      instanceId,
      sessionData,
      surveyId,
    }: {
      instanceId: string;
      sessionData: CreateSurveyResponseDTO;
      surveyId: string;
    }) => createAnonymousSession(instanceId, sessionData, surveyId),
    onSuccess: (data, variables) => {
      // Cache the session data
      queryClient.setQueryData(
        surveyResponseQueryKeys.session(variables.instanceId, data.sessionId),
        data
      );
    },
  });
};

/**
 * Hook to submit answer for a single question
 */
export const useSubmitAnswer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      instanceId,
      sessionId,
      answerData,
      surveyId,
    }: {
      instanceId: string;
      sessionId: string;
      answerData: SubmitAnswerDTO;
      surveyId: string;
    }) => submitAnswer(instanceId, sessionId, answerData, surveyId),
    onSuccess: (data, variables) => {
      // Update progress cache
      queryClient.setQueryData(
        surveyResponseQueryKeys.sessionProgress(variables.instanceId, variables.sessionId),
        data
      );

      // Invalidate response data to refetch with new answer
      queryClient.invalidateQueries({
        queryKey: surveyResponseQueryKeys.response(variables.instanceId, variables.sessionId),
      });
    },
  });
};

/**
 * Hook to complete survey response
 */
export const useCompleteSurveyResponse = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      instanceId,
      sessionId,
      surveyId,
    }: {
      instanceId: string;
      sessionId: string;
      surveyId: string;
    }) => completeSurveyResponse(instanceId, sessionId, surveyId),
    onSuccess: (data, variables) => {
      // Update all related queries
      queryClient.invalidateQueries({
        queryKey: surveyResponseQueryKeys.session(variables.instanceId, variables.sessionId),
      });
      queryClient.invalidateQueries({
        queryKey: surveyResponseQueryKeys.response(variables.instanceId, variables.sessionId),
      });
      queryClient.invalidateQueries({
        queryKey: surveyResponseQueryKeys.sessionProgress(
          variables.instanceId,
          variables.sessionId
        ),
      });
    },
  });
};

/**
 * Hook to get current survey response data
 */
export const useSurveyResponse = (
  instanceId: string,
  sessionId: string,
  surveyId: string,
  enabled = true
) => {
  return useQuery({
    queryKey: surveyResponseQueryKeys.response(instanceId, sessionId),
    queryFn: () => getSurveyResponse(instanceId, sessionId, surveyId),
    enabled: !!instanceId && !!sessionId && !!surveyId && enabled,
    staleTime: 1000 * 60, // 1 minute
  });
};
